/**
 * EntityRdfViewer
 *
 * Toolbar with Turtle / RDF/XML / JSON-LD buttons.
 * Serializes the already-loaded entity properties client-side —
 * no extra network request required.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EntityProperty, EntityObject } from '@/types';
import type { PrefixMap } from '@utils/prefix.utils';
import styles from './EntityPage.module.scss';

// ─── Serialization helpers ────────────────────────────────────────────────────

function escapeTurtle(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t');
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Return the most specific prefix match, or angle-bracket form. */
function angleOrPrefixed(uri: string, prefixes: PrefixMap): string {
  let bestPrefix = '';
  let bestNs = '';
  for (const [p, ns] of Object.entries(prefixes)) {
    if (uri.startsWith(ns) && ns.length > bestNs.length) {
      bestPrefix = p;
      bestNs = ns;
    }
  }
  if (bestNs) return `${bestPrefix}:${uri.slice(bestNs.length)}`;
  return `<${uri}>`;
}

function objToTurtle(obj: EntityObject, prefixes: PrefixMap): string {
  if (obj.type === 'uri') return angleOrPrefixed(obj.value, prefixes);
  if (obj.type === 'bnode') return `_:${obj.value}`;
  const escaped = `"${escapeTurtle(obj.value)}"`;
  if (obj.lang) return `${escaped}@${obj.lang}`;
  if (obj.datatype) return `${escaped}^^${angleOrPrefixed(obj.datatype, prefixes)}`;
  return escaped;
}

function serializeToTurtle(uri: string, properties: EntityProperty[], prefixes: PrefixMap): string {
  // Collect which prefixes are actually needed
  const used = new Set<string>();
  const markUri = (u: string) => {
    let bestNs = '';
    let bestPrefix = '';
    for (const [p, ns] of Object.entries(prefixes)) {
      if (u.startsWith(ns) && ns.length > bestNs.length) { bestPrefix = p; bestNs = ns; }
    }
    if (bestNs) used.add(bestPrefix);
  };
  markUri(uri);
  for (const prop of properties) {
    markUri(prop.predicate);
    for (const obj of prop.objects) {
      if (obj.type === 'uri') markUri(obj.value);
      if (obj.datatype) markUri(obj.datatype);
    }
  }

  const lines: string[] = [];
  for (const p of [...used].sort()) {
    lines.push(`@prefix ${p}: <${prefixes[p]}> .`);
  }
  if (used.size > 0) lines.push('');

  if (properties.length === 0) {
    lines.push(`<${uri}> .`);
    return lines.join('\n');
  }

  lines.push(`<${uri}>`);
  const triples: string[] = [];
  for (const prop of properties) {
    const pred = angleOrPrefixed(prop.predicate, prefixes);
    for (const obj of prop.objects) {
      triples.push(`    ${pred} ${objToTurtle(obj, prefixes)}`);
    }
  }
  for (let i = 0; i < triples.length; i++) {
    lines.push(triples[i] + (i < triples.length - 1 ? ' ;' : ' .'));
  }
  return lines.join('\n');
}

function serializeToRdfXml(uri: string, properties: EntityProperty[]): string {
  const nsMap: Record<string, string> = {
    rdf: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
  };
  let nsCounter = 0;

  function registerNs(predUri: string): { alias: string; local: string } | null {
    const hashIdx = predUri.lastIndexOf('#');
    const slashIdx = predUri.lastIndexOf('/');
    const splitIdx = Math.max(hashIdx, slashIdx);
    if (splitIdx < 0) return null;
    const ns = predUri.slice(0, splitIdx + 1);
    const local = predUri.slice(splitIdx + 1);
    // local name must be a valid XML name
    if (!local || !/^[a-zA-Z_][\w.-]*$/.test(local)) return null;
    const existing = Object.entries(nsMap).find(([, v]) => v === ns);
    if (existing) return { alias: existing[0], local };
    const alias = `ns${nsCounter++}`;
    nsMap[alias] = ns;
    return { alias, local };
  }

  // Pre-scan so nsMap is complete before building xml attrs
  for (const prop of properties) {
    registerNs(prop.predicate);
    for (const obj of prop.objects) {
      if (obj.datatype) registerNs(obj.datatype);
    }
  }

  const nsAttrs = Object.entries(nsMap)
    .map(([a, u]) => `xmlns:${a}="${escapeXml(u)}"`)
    .join('\n             ');

  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<rdf:RDF ${nsAttrs}>`,
    `  <rdf:Description rdf:about="${escapeXml(uri)}">`,
  ];

  for (const prop of properties) {
    const parsed = registerNs(prop.predicate);
    if (!parsed) continue;
    const { alias, local } = parsed;
    for (const obj of prop.objects) {
      if (obj.type === 'uri') {
        lines.push(`    <${alias}:${local} rdf:resource="${escapeXml(obj.value)}"/>`);
      } else if (obj.type === 'bnode') {
        lines.push(`    <${alias}:${local} rdf:nodeID="${escapeXml(obj.value)}"/>`);
      } else {
        const langAttr = obj.lang ? ` xml:lang="${obj.lang}"` : '';
        const dtAttr = obj.datatype && !obj.lang ? ` rdf:datatype="${escapeXml(obj.datatype)}"` : '';
        lines.push(
          `    <${alias}:${local}${langAttr}${dtAttr}>${escapeXml(obj.value)}</${alias}:${local}>`
        );
      }
    }
  }

  lines.push('  </rdf:Description>');
  lines.push('</rdf:RDF>');
  return lines.join('\n');
}

function objToJsonLdValue(obj: EntityObject): unknown {
  if (obj.type === 'uri') return { '@id': obj.value };
  if (obj.type === 'bnode') return { '@id': `_:${obj.value}` };
  if (obj.lang) return { '@value': obj.value, '@language': obj.lang };
  if (obj.datatype) return { '@value': obj.value, '@type': obj.datatype };
  return { '@value': obj.value };
}

function serializeToJsonLd(uri: string, properties: EntityProperty[], prefixes: PrefixMap): string {
  // Only include prefixes that match something in this entity's data
  const used: Record<string, string> = {};
  const markUri = (u: string) => {
    let bestNs = '';
    let bestPrefix = '';
    for (const [p, ns] of Object.entries(prefixes)) {
      if (u.startsWith(ns) && ns.length > bestNs.length) { bestPrefix = p; bestNs = ns; }
    }
    if (bestNs) used[bestPrefix] = bestNs;
  };
  for (const prop of properties) {
    markUri(prop.predicate);
    for (const obj of prop.objects) {
      if (obj.type === 'uri') markUri(obj.value);
    }
  }

  const doc: Record<string, unknown> = { '@context': used, '@id': uri };

  for (const prop of properties) {
    const key = angleOrPrefixed(prop.predicate, prefixes).replace(/^<|>$/g, '');
    const values = prop.objects.map(objToJsonLdValue);
    doc[key] = values.length === 1 ? values[0] : values;
  }

  return JSON.stringify(doc, null, 2);
}

// ─── Component ────────────────────────────────────────────────────────────────

interface RdfFormat {
  label: string;
  serialize: (uri: string, props: EntityProperty[], pfx: PrefixMap) => string;
}

const RDF_FORMATS: RdfFormat[] = [
  { label: 'Turtle', serialize: serializeToTurtle },
  { label: 'RDF/XML', serialize: (u, p) => serializeToRdfXml(u, p) },
  { label: 'JSON-LD', serialize: serializeToJsonLd },
];

interface EntityRdfViewerProps {
  uri: string;
  properties: EntityProperty[];
  prefixes: PrefixMap;
}

const EntityRdfViewer = ({ uri, properties, prefixes }: EntityRdfViewerProps): JSX.Element => {
  const { t } = useTranslation();
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const activeFormat = RDF_FORMATS.find((f) => f.label === activeLabel) ?? null;

  const content = useMemo(
    () => activeFormat?.serialize(uri, properties, prefixes) ?? '',
    [activeFormat, uri, properties, prefixes]
  );

  function toggleFormat(fmt: RdfFormat) {
    setActiveLabel((prev) => (prev === fmt.label ? null : fmt.label));
    setCopied(false);
  }

  async function copyContent() {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  }

  function downloadContent() {
    if (!content || !activeFormat) return;
    try {
      const ext: Record<string, string> = { 'Turtle': 'ttl', 'RDF/XML': 'rdf', 'JSON-LD': 'jsonld' };
      const filename = `${uri.split(/[#/]/).filter(Boolean).pop() || 'entity'}.${ext[activeFormat.label] || 'txt'}`;
      
      // Add UTF-8 BOM for proper encoding in Excel and other applications
      const bom = '\uFEFF';
      const textWithBom = bom + content;
      const blob = new Blob([textWithBom], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[EntityRdfViewer] Download failed:', error);
    }
  }

  return (
    <div className={styles.rdfViewer}>
      <div className={styles.rdfToolbar}>
        {RDF_FORMATS.map((fmt) => (
          <button
            key={fmt.label}
            className={`${styles.rdfButton} ${activeLabel === fmt.label ? styles.rdfButtonActive : ''}`}
            onClick={() => toggleFormat(fmt)}
            aria-pressed={activeLabel === fmt.label}
          >
            {fmt.label}
          </button>
        ))}
      </div>

      {activeFormat && (
        <div className={styles.rdfCodeViewer}>
          <div className={styles.rdfCodeHeader}>
            <span className={styles.rdfCodeTitle}>{activeFormat.label}</span>
            <div className={styles.rdfCodeActions}>
              <button
                className={styles.rdfIconButton}
                onClick={downloadContent}
                title={t('entity.rdf.download')}
              >
                ⬇
              </button>
              <button
                className={styles.rdfIconButton}
                onClick={copyContent}
                title={copied ? t('entity.rdf.copied') : t('entity.rdf.copy')}
              >
                {copied ? '✓' : '⎘'}
              </button>
              <button
                className={styles.rdfIconButton}
                onClick={() => setActiveLabel(null)}
                title={t('entity.rdf.close')}
              >
                ✕
              </button>
            </div>
          </div>
          <pre className={styles.rdfCode}>
            <code>{content}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

export default EntityRdfViewer;
